import { NumericFormat } from 'react-number-format'
import Num2persian from 'utilities/helper/num2Persian'
import { toman } from 'utilities/helper/format'

const QUICK_FRACTIONS = [0.25, 0.5, 1]

/**
 * «چه مبلغی؟» — capped at سرمایهٔ شخصی, and at nothing else.
 *
 * Two things are deliberately NOT enforced here:
 *
 * - **هبه does not raise the ceiling.** A member with 5,000,000 capital and
 *   300,000 هبه may ask for 5,000,000. The gift is the fund's money now, and
 *   the card says so rather than letting them find out at submit.
 * - **Debt does not lower it.** A member who owes the fund is stated, never
 *   blocked, and the figure they see is still their whole capital. Funds lend
 *   to people in difficulty on purpose; whether to net the debt off is the
 *   manager's decision on their own screen, not a validation rule that quietly
 *   shrinks what the member is allowed to ask for.
 */
export default function StepAmount({
    capital,
    debt,
    amount,
    onChange,
}: {
    capital: number
    debt: number
    amount: string
    onChange: (value: string) => void
}) {
    const numeric = Number(amount || 0)
    const over = numeric > capital

    return (
        <div>
            <div className=" rounded-2xl border border-stroke bg-white p-4">
                <p className=" text-xs text-text-secondary">
                    سرمایهٔ شما در این صندوق
                </p>
                <p className=" mt-1 text-xl font-bold text-heading">
                    {toman(capital)}
                </p>

                <NumericFormat
                    value={amount}
                    thousandSeparator
                    allowNegative={false}
                    inputMode="numeric"
                    placeholder="مبلغ درخواست"
                    onValueChange={(values) => onChange(values.value)}
                    className=" mt-4 w-full rounded-xl border border-stroke bg-canvas px-3 py-3 text-center text-lg font-bold text-heading outline-none focus:border-brand"
                />

                {numeric > 0 && (
                    <p className=" mt-2 text-center text-xs text-blue-1">
                        {Num2persian(String(numeric))} تومان
                    </p>
                )}

                {over && (
                    <p className=" mt-2 text-center text-sm text-error-3">
                        بیشتر از سرمایهٔ شما در این صندوق است.
                    </p>
                )}

                <div className=" mt-4 flex gap-2">
                    {QUICK_FRACTIONS.map((fraction) => (
                        <button
                            key={fraction}
                            type="button"
                            onClick={() =>
                                onChange(
                                    String(Math.floor(capital * fraction))
                                )
                            }
                            className=" flex-1 rounded-full border border-stroke py-2 text-[11px] font-bold text-text-body"
                        >
                            {fraction === 1
                                ? 'همهٔ سرمایه'
                                : `${Num2persian(String(fraction * 100))} درصد`}
                        </button>
                    ))}
                </div>
            </div>

            {debt > 0 && (
                <div className=" mt-3 rounded-2xl bg-warning-soft px-3 py-3 text-[11px] leading-6 text-warning-on">
                    شما {toman(debt)} بابت اقساط پرداخت‌نشده به این صندوق بدهکار
                    هستید. می‌توانید هر مبلغی تا سقف سرمایه‌تان درخواست دهید؛
                    مدیر صندوق این بدهی را می‌بیند و ممکن است مبلغ کمتری پرداخت
                    کند.
                </div>
            )}
        </div>
    )
}
